<script setup>
import { router } from '@inertiajs/vue3'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import GenerationPanel from '@/components/admin/market-overviews/GenerationPanel.vue'
import NationalOverviewCards from '@/components/admin/market-overviews/NationalOverviewCards.vue'
import OverviewDrawer from '@/components/admin/market-overviews/OverviewDrawer.vue'
import OverviewMatrix from '@/components/admin/market-overviews/OverviewMatrix.vue'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

// RED-317 §8 — Admin → Turuülevaated for the admin's own company: the
// universal-mall generation panel, the national overviews, and the county ×
// purpose matrix. A cell opens in the drawer. `generation` and `overviews`
// are closure props, so the generation poll reloads only them.
const props = defineProps({
  vocabulary: { type: Object, required: true },
  overviews: { type: Array, required: true },
  generation: { type: Object, required: true },
})

const { t } = useI18n()

const cellKey = (county, purpose) => `${county ?? ''}|${purpose}`

const overviewsByCell = computed(() =>
  Object.fromEntries(props.overviews.map(overview => [cellKey(overview.county, overview.purpose), overview])),
)

const regionalPurposes = computed(() => props.vocabulary.purposes.filter(purpose => !purpose.national))
// Majandusülevaade (the report's §4.1) leads, as in the report.
const nationalPurposes = computed(() =>
  props.vocabulary.purposes
    .filter(purpose => purpose.national)
    .sort((a, b) => Number(b.section === 'economy') - Number(a.section === 'economy')),
)
const purposeLabels = computed(() => Object.fromEntries(props.vocabulary.purposes.map(purpose => [purpose.id, purpose.label])))

/** Freshness over the matrix (15 × 8); the national cards carry their own tags. */
const counts = computed(() => {
  const tally = { fresh: 0, stale: 0, missing: 0 }
  for (const county of props.vocabulary.counties) {
    for (const purpose of regionalPurposes.value) {
      tally[overviewsByCell.value[cellKey(county.value, purpose.id)]?.status ?? 'missing']++
    }
  }
  return tally
})

const filter = ref('all')
const filterOptions = computed(() => [
  { value: 'all', label: t('messages.admin.market_overviews.filter.all') },
  { value: 'stale', label: t('messages.admin.market_overviews.filter.stale') },
  { value: 'missing', label: t('messages.admin.market_overviews.filter.missing') },
])

const COUNT_DOTS = { fresh: 'bg-green-600', stale: 'bg-amber-500', missing: 'bg-gray-300' }

const selected = ref(null)
const drawerVisible = ref(false)
const selectedKey = computed(() => (drawerVisible.value && selected.value ? cellKey(selected.value.county, selected.value.purpose) : null))
const selectedOverview = computed(() => (selected.value ? (overviewsByCell.value[cellKey(selected.value.county, selected.value.purpose)] ?? null) : null))

const selectedPurpose = computed(() => props.vocabulary.purposes.find(purpose => purpose.id === selected.value?.purpose) ?? null)

/** Counties whose text of the selected purpose is published: "Alusta teise maakonna tekstist". */
const sourceCounties = computed(() => {
  if (!selected.value?.county || selectedPurpose.value?.section !== 'market') {
    return []
  }
  return props.vocabulary.counties
    .filter(county => county.value !== selected.value.county && overviewsByCell.value[cellKey(county.value, selected.value.purpose)]?.current)
    .map(county => ({ value: county.value, label: county.label }))
})

const drawer = useTemplateRef('drawer')

// Pending edits are saved before another cell opens; a conflict or a failed
// save keeps the drawer on its cell, with the reason shown there.
const select = async cell => {
  if (drawerVisible.value && !(await drawer.value?.flush())) {
    return
  }
  selected.value = cell
  drawerVisible.value = true
}

/** A draft started, imported, published or discarded: refresh the matrix. */
const refreshOverviews = () => router.reload({ only: ['overviews'] })
</script>

<template>
  <div class="mx-auto max-w-7xl space-y-6 py-4" data-testid="mo-page">
    <GenerationPanel :purpose-labels="purposeLabels" />

    <div class="flex flex-wrap items-end justify-between gap-4">
      <div class="max-w-3xl">
        <h2 class="text-xl font-medium text-gray-800">{{ $t('messages.admin.market_overviews.title') }}</h2>
        <p class="mt-1 text-sm text-gray-500">{{ $t('messages.admin.market_overviews.intro') }}</p>
      </div>
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-3 text-sm text-gray-700" data-testid="mo-counts">
          <span v-for="status in ['fresh', 'stale', 'missing']" :key="status" class="inline-flex items-center gap-1.5" :data-testid="`mo-count-${status}`">
            <span class="inline-block size-2 rounded-full" :class="COUNT_DOTS[status]" />
            {{ $t(`messages.admin.market_overviews.counts.${status}`, { n: counts[status] }) }}
          </span>
        </div>
        <SelectButton
          v-model="filter"
          :options="filterOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          :aria-label="$t('messages.admin.market_overviews.filter.label')"
          size="small"
          data-testid="mo-filter"
        />
      </div>
    </div>

    <NationalOverviewCards
      :purposes="nationalPurposes"
      :overviews-by-cell="overviewsByCell"
      :selected-key="selectedKey"
      @select="select"
    />

    <OverviewMatrix
      :counties="vocabulary.counties"
      :groups="vocabulary.groups"
      :purposes="regionalPurposes"
      :overviews-by-cell="overviewsByCell"
      :selected-key="selectedKey"
      :filter="filter"
      @select="select"
    />

    <OverviewDrawer
      ref="drawer"
      v-model:visible="drawerVisible"
      :cell="selected"
      :overview="selectedOverview"
      :county-label="selected?.county ?? null"
      :purpose-label="selected ? (purposeLabels[selected.purpose] ?? selected.purpose) : ''"
      :section="selectedPurpose?.section ?? 'market'"
      :source-counties="sourceCounties"
      :trykis="selectedPurpose?.trykis ?? null"
      @changed="refreshOverviews"
    />
  </div>
</template>
